/**
 * questManager.ts
 * 
 * Dynamic Quest State Management
 * Handles terrain-based quests with on-demand section generation
 */

import { getFirestore, doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AxialCoord } from '../types/terrain';
import { getSponsoredQuestForHex, sponsorQuestToHex } from '../services/globalHexExploration';

const db = getFirestore();
const functions = getFunctions();

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Quest {
  id: string;
  terrainType: string;
  worldTheme: string;
  type: 'lore' | 'faction';
  synopsis?: string; // Quest synopsis for hybrid generation (narrative arc)
  revelationSize?: 'minor' | 'moderate' | 'major';
  revelationText: string;
  revelationTitle: string;
  loreUpdated: boolean;
  discoveredBy: string | null;
  completedBy: string[];
  abandonedBy: string[];
  createdBy: string;
  createdAt: any;
  factionId: string | null;
  isSponsored?: boolean; // true if sponsored, false/undefined if not
  sponsoredBy?: string; // userId who sponsored
  sponsoredAt?: any; // timestamp
  sponsoredHex?: { q: number; r: number }; // hex where sponsored
}

export interface QuestPlaythrough {
  id: string;
  playerId: string;
  partyId: string | null;
  questId: string;
  terrainType: string;
  currentSection: number;
  maxSections: number; // Always 12
  startHex: { q: number; r: number };
  visitedHexes: Array<{ q: number; r: number }>;
  questState: Record<string, any>;
  status: 'active' | 'completed' | 'abandoned';
  lastPlayedAt: any;
  createdAt: any;
  
  // NEW: NPC and Choice tracking for narrative coherence
  choiceHistory?: Array<{ section: number; choiceId: string; choiceText: string }>; // Track all choices made
  
  // Legacy fields for backward compatibility (deprecated)
  campaignId?: string; // @deprecated Use questId instead
  currentQuestIndex?: number; // @deprecated Always 0 in new system
  sections?: QuestSection[]; // @deprecated Sections not stored in playthrough
}

export interface QuestSection {
  sectionNumber: number;
  narrative: string;
  audioUrl?: string;
  choices: QuestChoice[];
  concludes: boolean;
  outcome?: string | null;
  hexLocation: { q: number; r: number };
  generatedAt: string;
  convergencePhase: string;
  isBottleneck: boolean;
  hasCombat?: boolean;
  monsterName?: string;
  monsterStats?: {
    hp: number;
    attackDie: number;
    damageDie: number;
    // Legacy format for backward compatibility
    attack?: number;
    damage?: number;
  };
  nextQuestHex?: { q: number; r: number; terrain?: string; direction?: string }; // Legacy field
}

export interface QuestChoice {
  id: string;
  text: string;
  direction: string | null;
  targetHex: { q: number; r: number };
  stateChanges?: Record<string, any>;
  requiresState?: Record<string, any>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Transform backend quest section format to frontend format
 * Backend uses: combatEncounter, monster
 * Frontend expects: hasCombat, monsterName, monsterStats
 */
function transformQuestSection(backendSection: any): QuestSection {
  const section: QuestSection = {
    sectionNumber: backendSection.sectionNumber,
    narrative: backendSection.narrative,
    audioUrl: backendSection.audioUrl,
    choices: backendSection.choices || [],
    concludes: backendSection.concludes || false,
    outcome: backendSection.outcome || null,
    hexLocation: backendSection.hexLocation || { q: 0, r: 0 },
    generatedAt: backendSection.generatedAt || new Date().toISOString(),
    convergencePhase: backendSection.convergencePhase || '',
    isBottleneck: backendSection.isBottleneck || false,
    // Transform combatEncounter → hasCombat
    hasCombat: backendSection.combatEncounter || false,
    // Transform monster → monsterName + monsterStats
    monsterName: backendSection.monster?.name,
    monsterStats: backendSection.monster ? {
      hp: backendSection.monster.hp,
      attackDie: backendSection.monster.attackDie,
      damageDie: backendSection.monster.damageDie
    } : undefined,
    nextQuestHex: backendSection.nextQuestHex
  };
  
  return section;
}

// ============================================================================
// QUEST GENERATION
// ============================================================================

/**
 * Get or generate quest for terrain type
 * Excludes quests the player has already completed or abandoned
 */
export async function getOrGenerateQuest(
  terrainType: string,
  startHex: AxialCoord,
  faction: string | null,
  partyComposition: string[],
  partyStamina: number,
  worldTheme: string = 'brynlore',
  userId?: string,
  excludeQuestIds?: string[] // Client-side exclusion list for recently abandoned quests
): Promise<{ quest: Quest; section1: QuestSection; wasGenerated: boolean; cacheHit: boolean; isUnexploredHex?: boolean }> {
  try {
    console.log(`\n🎯 ============ GETTING OR GENERATING QUEST ============`);
    console.log(`   Terrain: ${terrainType}`);
    console.log(`   Hex: (${startHex.q}, ${startHex.r})`);
    console.log(`   User ID: ${userId || 'NONE'}`);
    console.log(`   World Theme: ${worldTheme}`);
    
    // STEP 1: Check if a quest is sponsored to this hex
    const sponsoredQuestId = await getSponsoredQuestForHex(startHex);
    
    if (sponsoredQuestId) {
      console.log(`\n🎯 ========== SPONSORED QUEST FOUND ==========`);
      console.log(`   Quest ${sponsoredQuestId} is sponsored to hex (${startHex.q}, ${startHex.r})`);
      console.log(`   Returning sponsored quest...`);
      
      // Get the sponsored quest
      const questDoc = await getDoc(doc(db, 'quests', sponsoredQuestId));
      
      if (questDoc.exists()) {
        const quest = { id: questDoc.id, ...questDoc.data() } as Quest;
        
        // Get Section 1
        const cacheKey = `${quest.id}-s1`;
        const sectionsRef = collection(db, 'questSections');
        const sectionQuery = query(
          sectionsRef,
          where('cacheKey', '==', cacheKey),
          limit(1)
        );
        
        const cachedSections = await getDocs(sectionQuery);
        
        if (!cachedSections.empty) {
          const section1 = transformQuestSection(cachedSections.docs[0].data());
          
          console.log(`✅ Returning sponsored quest with Section 1`);
          console.log(`============================================\n`);
          
          return {
            quest,
            section1,
            wasGenerated: false,
            cacheHit: true
          };
        }
      }
      
      console.log(`⚠️ Sponsored quest ${sponsoredQuestId} not found or missing Section 1`);
      console.log(`   Falling through to normal quest lookup...`);
    }
    
    // STEP 2: Normal quest lookup (existing logic)
    if (excludeQuestIds && excludeQuestIds.length > 0) {
      console.log(`\n🚫 CLIENT-SIDE EXCLUSION LIST (${excludeQuestIds.length}):`);
      excludeQuestIds.forEach(id => console.log(`   - ${id}`));
    } else {
      console.log(`\n📝 No client-side exclusions`);
    }
    
    // Get ALL quests for this terrain (not just first one)
    console.log(`\n🔍 Querying Firestore for ${terrainType} quests...`);
    const questsRef = collection(db, 'quests');
    const q = query(
      questsRef,
      where('terrainType', '==', terrainType),
      where('worldTheme', '==', worldTheme)
    );
    
    const existingQuests = await getDocs(q);
    console.log(`📊 Found ${existingQuests.docs.length} total ${terrainType} quests in database`);
    
    if (!existingQuests.empty) {
      console.log(`\n🔎 ========== FILTERING QUESTS ==========`);
      
      // Filter out quests the player has already completed or abandoned
      const availableQuests = existingQuests.docs.filter(doc => {
        const quest = doc.data();
        const questId = doc.id;
        
        console.log(`\n📋 Quest ${questId}:`);
        console.log(`   completedBy: [${(quest.completedBy || []).join(', ')}]`);
        console.log(`   abandonedBy: [${(quest.abandonedBy || []).join(', ')}]`);
        console.log(`   isSponsored: ${quest.isSponsored || false}`);
        
        // Filter out completed but unsponsored quests (unavailable for replay)
        if (quest.completedBy && quest.completedBy.length > 0 && !quest.isSponsored) {
          console.log(`   ❌ EXCLUDED: Quest completed but not sponsored (unavailable)`);
          return false;
        }
        
        // First check client-side exclusion list (immediate)
        if (excludeQuestIds && excludeQuestIds.includes(questId)) {
          console.log(`   ❌ EXCLUDED: In client-side exclusion cache`);
          return false;
        }
        
        if (!userId) {
          console.log(`   ✅ AVAILABLE: No userId to check against`);
          return true; // If no userId, allow any quest
        }
        
        const completedBy = quest.completedBy || [];
        const abandonedBy = quest.abandonedBy || [];
        
        const isCompleted = completedBy.includes(userId);
        const isAbandoned = abandonedBy.includes(userId);
        
        if (isCompleted) {
          console.log(`   ❌ EXCLUDED: User ${userId} has completed this quest`);
          return false;
        }
        
        if (isAbandoned) {
          console.log(`   ❌ EXCLUDED: User ${userId} has abandoned this quest`);
          return false;
        }
        
        console.log(`   ✅ AVAILABLE: User has not played this quest`);
        return true;
      });
      
      console.log(`\n📋 ========== FILTERING COMPLETE ==========`);
      console.log(`   Total quests: ${existingQuests.docs.length}`);
      console.log(`   Available quests: ${availableQuests.length}`);
      console.log(`   Filtered out: ${existingQuests.docs.length - availableQuests.length}`);
      
      if (availableQuests.length > 0) {
        // Return first available quest
        const questDoc = availableQuests[0];
        const quest = { id: questDoc.id, ...questDoc.data() } as Quest;
        
        console.log(`\n✅ ========== RETURNING EXISTING QUEST ==========`);
        console.log(`   Quest ID: ${quest.id}`);
        console.log(`   Terrain: ${quest.terrainType}`);
        console.log(`   Type: ${quest.type}`);
        console.log(`============================================\n`);
        
        // Get cached Section 1 for this specific quest
        const cacheKey = `${quest.id}-s1`;
        const sectionsRef = collection(db, 'questSections');
        const sectionQuery = query(
          sectionsRef,
          where('cacheKey', '==', cacheKey),
          limit(1)
        );
        
        const cachedSections = await getDocs(sectionQuery);
        
        if (!cachedSections.empty) {
          const sectionDoc = cachedSections.docs[0];
          const section1 = transformQuestSection(sectionDoc.data());
          
          console.log(`🎵 Section 1 audioUrl:`, section1.audioUrl || 'NONE');
          
          return {
            quest,
            section1,
            wasGenerated: false,
            cacheHit: true
          };
        }
        
        // Quest exists but Section 1 not cached - need to generate section
        console.log(`⚠️ Quest ${quest.id} exists but Section 1 not cached`);
      } else {
        console.log(`\n⚠️ NO AVAILABLE QUESTS`);
        console.log(`   All ${existingQuests.docs.length} ${terrainType} quests have been completed or abandoned by this user`);
        console.log(`   Will generate a new quest...`);
      }
    }
    
    // Generate new quest
    console.log(`\n🎨 ========== GENERATING NEW QUEST ==========`);
    console.log(`   Terrain: ${terrainType}`);
    console.log(`   Reason: ${existingQuests.empty ? 'No quests exist' : 'All quests played'}`);
    
    // Use Fighting Fantasy quest generation system
    const generateQuestFF = httpsCallable(functions, 'generateQuestFF');
    const result = await generateQuestFF({
      terrainType,
      startHex,
      faction,
      worldTheme,
      partyComposition,
      partyStamina
    });
    
    const data = result.data as any;
    
    if (!data.success) {
      throw new Error('Quest generation failed');
    }
    
    console.log(`\n✅ ========== NEW QUEST GENERATED ==========`);
    console.log(`   Quest ID: ${data.quest.id}`);
    console.log(`   Terrain: ${data.quest.terrainType}`);
    console.log(`   Type: ${data.quest.type}`);
    console.log(`   Cost: $${data.cost?.amount.toFixed(4) || '0.0000'}`);
    console.log(`============================================\n`);
    
    return {
      quest: data.quest,
      section1: transformQuestSection(data.section1),
      wasGenerated: true,
      cacheHit: false
    };
    
  } catch (error) {
    console.error('Error in getOrGenerateQuest:', error);
    throw error;
  }
}

/**
 * Get or generate section
 */
export async function getOrGenerateSection(
  questId: string,
  terrainType: string,
  sectionNumber: number,
  previousChoiceId: string | null,
  currentHex: AxialCoord,
  adjacentHexes: Array<{ q: number; r: number; terrain: string; direction: string }>,
  questState: Record<string, any>,
  worldTheme: string = 'brynlore',
  monsterFrequency: number = 1/12,
  combatHistory: Array<{ sectionNumber: number; monsterName: string }> = [],
  playthroughId: string | null = null
): Promise<{ section: QuestSection; wasGenerated: boolean; cacheHit: boolean }> {
  try {
    console.log(`📖 Getting or generating Section ${sectionNumber} for ${terrainType}`);
    
    // Build cache key - MUST include questId to prevent cross-quest contamination
    const cacheKey = previousChoiceId 
      ? `${questId}-s${sectionNumber}-c${previousChoiceId}`
      : `${questId}-s${sectionNumber}`;
    
    // Check cache
    const sectionsRef = collection(db, 'questSections');
    const cacheQuery = query(
      sectionsRef,
      where('cacheKey', '==', cacheKey),
      limit(1)
    );
    
    const cachedSections = await getDocs(cacheQuery);
    
    if (!cachedSections.empty) {
      const sectionDoc = cachedSections.docs[0];
      const section = transformQuestSection(sectionDoc.data());
      
      console.log(`✅ Cache hit: ${cacheKey}`);
      
      return {
        section,
        wasGenerated: false,
        cacheHit: true
      };
    }
    
    // Generate new section using Fighting Fantasy system
    console.log(`🎨 Generating Section ${sectionNumber} (cache miss: ${cacheKey})`);
    
    // Fetch choice history from playthrough if available
    let choiceHistory: Array<{ section: number; choice: string }> = [];
    if (playthroughId) {
      try {
        const playthrough = await getQuestPlaythroughById(playthroughId);
        if (playthrough?.choiceHistory) {
          choiceHistory = playthrough.choiceHistory.map(ch => ({
            section: ch.section,
            choice: ch.choiceText
          }));
          console.log(`📜 Loaded ${choiceHistory.length} choices from playthrough history`);
        }
      } catch (error) {
        console.warn('Could not load choice history from playthrough:', error);
      }
    }
    
  const generateSectionFF = httpsCallable(functions, 'generateSectionFF');
  const result = await generateSectionFF({
    questId,
    terrainType,
    sectionNumber,
    previousChoiceId,
    currentHex,
    adjacentHexes,
    questState,
    worldTheme,
    choiceHistory
  });
    
    const data = result.data as any;
    
    if (!data.success) {
      throw new Error('Section generation failed');
    }
    
    console.log(`✅ Section ${sectionNumber} generated`);
    console.log(`💰 Cost: $${data.cost?.amount.toFixed(4) || '0.0000'}`);
    
    return {
      section: transformQuestSection(data.section),
      wasGenerated: true,
      cacheHit: false
    };
    
  } catch (error) {
    console.error('Error in getOrGenerateSection:', error);
    throw error;
  }
}

// ============================================================================
// QUEST PLAYTHROUGH MANAGEMENT
// ============================================================================

/**
 * Start a new quest playthrough
 */
export async function startQuestPlaythrough(
  userId: string,
  questId: string,
  terrainType: string,
  startHex: AxialCoord
): Promise<QuestPlaythrough> {
  try {
    const playthroughRef = doc(collection(db, 'questPlaythroughs'));
    
    const playthrough: QuestPlaythrough = {
      id: playthroughRef.id,
      playerId: userId,
      partyId: null,
      questId,
      terrainType,
      currentSection: 1,
      maxSections: 12,
      startHex,
      visitedHexes: [startHex],
      questState: {},
      status: 'active',
      lastPlayedAt: Timestamp.now(),
      createdAt: Timestamp.now()
    };
    
    await setDoc(playthroughRef, playthrough);
    
    console.log(`✅ Quest playthrough started: ${playthrough.id}`);
    
    return playthrough;
    
  } catch (error) {
    console.error('Error starting quest playthrough:', error);
    throw error;
  }
}

/**
 * Get active quest playthrough for player
 */
export async function getActiveQuestPlaythrough(userId: string): Promise<QuestPlaythrough | null> {
  try {
    const playthroughsRef = collection(db, 'questPlaythroughs');
    const q = query(
      playthroughsRef,
      where('playerId', '==', userId),
      where('status', '==', 'active'),
      orderBy('lastPlayedAt', 'desc'),
      limit(1)
    );
    
    const playthroughs = await getDocs(q);
    
    if (playthroughs.empty) {
      return null;
    }
    
    const playthroughDoc = playthroughs.docs[0];
    return { id: playthroughDoc.id, ...playthroughDoc.data() } as QuestPlaythrough;
    
  } catch (error) {
    console.error('Error getting active quest playthrough:', error);
    return null;
  }
}

/**
 * Load all sections for a quest playthrough from the cache
 * Used when resuming a quest to hydrate the playthrough with section content
 * 
 * Cache key format:
 * - Section 1: questId-terrainType-s1
 * - Section 2+: questId-terrainType-sN-cCHOICEID
 */
export async function loadSectionsForPlaythrough(playthrough: QuestPlaythrough): Promise<QuestSection[]> {
  try {
    const sections: QuestSection[] = [];
    const questId = playthrough.questId;
    const terrainType = playthrough.terrainType;
    const currentSection = playthrough.currentSection;
    const choiceHistory = playthrough.choiceHistory || [];
    
    console.log(`📚 Loading sections 1-${currentSection} for quest ${questId} (${terrainType})`);
    console.log(`📝 Choice history:`, choiceHistory);
    
    // Load all sections from 1 to currentSection
    for (let i = 1; i <= currentSection; i++) {
      let cacheKey: string;
      
      if (i === 1) {
        // Section 1: questId-terrainType-s1
        cacheKey = `${questId}-${terrainType}-s1`;
      } else {
        // Section 2+: questId-terrainType-sN-cCHOICEID
        // The choice that led to section i is at choiceHistory[i-2]
        // (section 1 is at index 0, section 2 choice is at index 1, etc)
        const previousChoice = choiceHistory[i - 2];
        if (!previousChoice) {
          console.warn(`  ⚠️ No choice history for section ${i}, cannot build cache key`);
          continue;
        }
        cacheKey = `${questId}-${terrainType}-s${i}-c${previousChoice.choiceId}`;
      }
      
      const sectionsRef = collection(db, 'questSections');
      const sectionQuery = query(
        sectionsRef,
        where('cacheKey', '==', cacheKey),
        limit(1)
      );
      
      const cachedSections = await getDocs(sectionQuery);
      
      if (!cachedSections.empty) {
        const sectionData = cachedSections.docs[0].data();
        const section = transformQuestSection(sectionData);
        sections.push(section);
        console.log(`  ✅ Loaded section ${i} (cacheKey: ${cacheKey})`);
      } else {
        console.warn(`  ⚠️ Section ${i} not found in cache (cacheKey: ${cacheKey})`);
      }
    }
    
    console.log(`📚 Loaded ${sections.length}/${currentSection} sections`);
    return sections;
    
  } catch (error) {
    console.error('Error loading sections for playthrough:', error);
    return [];
  }
}

/**
 * Update quest playthrough progress
 */
export async function updateQuestPlaythrough(
  playthroughId: string,
  updates: Partial<QuestPlaythrough>
): Promise<void> {
  try {
    const playthroughRef = doc(db, 'questPlaythroughs', playthroughId);
    
    await updateDoc(playthroughRef, {
      ...updates,
      lastPlayedAt: Timestamp.now()
    });
    
    console.log(`✅ Quest playthrough updated: ${playthroughId}`);
    
  } catch (error) {
    console.error('Error updating quest playthrough:', error);
    throw error;
  }
}

/**
 * Complete quest playthrough
 * Marks quest as completed - sponsorship decision handled separately by UI
 */
export async function completeQuestPlaythrough(
  playthroughId: string,
  questId: string,
  userId: string
): Promise<void> {
  try {
    // Update playthrough status only
    // The Firestore trigger (onQuestPlaythroughUpdate) will automatically update
    // the quest's completedBy array server-side
    // Sponsorship will be handled by QuestSectionDisplay UI
    const playthroughRef = doc(db, 'questPlaythroughs', playthroughId);
    await updateDoc(playthroughRef, {
      status: 'completed',
      lastPlayedAt: Timestamp.now()
    });
    
    console.log(`✅ Quest playthrough completed (quest metadata will be updated by server trigger): ${playthroughId}`);
    console.log(`⏳ Awaiting sponsorship decision from player`);
    
  } catch (error) {
    console.error('Error completing quest playthrough:', error);
    throw error;
  }
}

/**
 * Abandon quest playthrough
 */
export async function abandonQuestPlaythrough(
  playthroughId: string,
  questId: string,
  userId: string
): Promise<{ questId: string }> {
  try {
    console.log(`🚫 ============ ABANDONING QUEST ============`);
    console.log(`   Playthrough ID: ${playthroughId}`);
    console.log(`   Quest ID: ${questId}`);
    console.log(`   User ID: ${userId}`);
    
    // Update playthrough status
    // The Firestore trigger (onQuestPlaythroughUpdate) will automatically update
    // the quest's abandonedBy array server-side
    const playthroughRef = doc(db, 'questPlaythroughs', playthroughId);
    
    console.log(`📝 Updating playthrough status to 'abandoned'...`);
    await updateDoc(playthroughRef, {
      status: 'abandoned',
      lastPlayedAt: Timestamp.now()
    });
    
    console.log(`✅ Playthrough status updated in Firestore`);
    console.log(`⏳ Firestore trigger will update quest.abandonedBy array asynchronously`);
    console.log(`🔄 Returning questId for immediate client-side exclusion: ${questId}`);
    console.log(`============================================`);
    
    // Return questId so caller can add it to exclusion list immediately
    return { questId };
    
  } catch (error) {
    console.error('❌ Error abandoning quest playthrough:', error);
    throw error;
  }
}

/**
 * Sponsor a completed quest - binds it to hex and makes it replayable
 */
export async function sponsorQuest(
  questId: string,
  userId: string,
  hex: AxialCoord
): Promise<void> {
  try {
    console.log(`💎 ============ SPONSORING QUEST ============`);
    console.log(`   Quest ID: ${questId}`);
    console.log(`   User ID: ${userId}`);
    console.log(`   Hex: (${hex.q}, ${hex.r})`);
    
    // Update quest document
    const questRef = doc(db, 'quests', questId);
    await updateDoc(questRef, {
      isSponsored: true,
      sponsoredBy: userId,
      sponsoredAt: Timestamp.now(),
      sponsoredHex: hex
    });
    
    console.log(`✅ Quest marked as sponsored`);
    
    // Bind quest to hex
    await sponsorQuestToHex(hex, questId, userId);
    
    console.log(`✅ Quest bound to hex - future players will replay this quest`);
    console.log(`============================================`);
    
  } catch (error) {
    console.error('❌ Error sponsoring quest:', error);
    throw error;
  }
}

/**
 * Mark quest as completed but not sponsored
 * Quest remains in DB but is unavailable for replay
 */
export async function markQuestUnsponsored(
  questId: string
): Promise<void> {
  try {
    console.log(`📝 ============ MARKING QUEST UNSPONSORED ============`);
    console.log(`   Quest ID: ${questId}`);
    
    // Update quest document
    const questRef = doc(db, 'quests', questId);
    await updateDoc(questRef, {
      isSponsored: false,
      // Don't add sponsoredBy, sponsoredAt, or sponsoredHex
    });
    
    console.log(`✅ Quest marked as completed but not sponsored`);
    console.log(`   Quest preserved in DB for content generation`);
    console.log(`   Quest unavailable for future players`);
    console.log(`====================================================`);
    
  } catch (error) {
    console.error('❌ Error marking quest as unsponsored:', error);
    throw error;
  }
}

// ============================================================================
// QUEST QUERIES
// ============================================================================

/**
 * Get quest by ID
 */
export async function getQuestById(questId: string): Promise<Quest | null> {
  try {
    const questDoc = await getDoc(doc(db, 'quests', questId));
    
    if (!questDoc.exists()) {
      return null;
    }
    
    return { id: questDoc.id, ...questDoc.data() } as Quest;
    
  } catch (error) {
    console.error('Error getting quest by ID:', error);
    return null;
  }
}

/**
 * Get quest playthrough by ID
 */
export async function getQuestPlaythroughById(playthroughId: string): Promise<QuestPlaythrough | null> {
  try {
    const playthroughDoc = await getDoc(doc(db, 'questPlaythroughs', playthroughId));
    
    if (!playthroughDoc.exists()) {
      return null;
    }
    
    return { id: playthroughDoc.id, ...playthroughDoc.data() } as QuestPlaythrough;
    
  } catch (error) {
    console.error('Error getting quest playthrough by ID:', error);
    return null;
  }
}

// ============================================================================
// LEGACY COMPATIBILITY (Deprecated - will be removed)
// ============================================================================

// Keep old Campaign interface for backward compatibility during migration
export interface Campaign {
  id: string;
  hexId: string;
  worldTheme: string;
  type: 'lore' | 'faction';
  revelationSize?: 'minor' | 'moderate' | 'major';
  synopsis: string;
  surfaceGoal: string;
  quests: Array<{
    title: string;
    synopsis: string;
    type: string;
    estimatedSections: number;
    startHex?: { q: number; r: number };
  }>;
  revelationTitle: string;
  revelationText: string;
  loreUpdated: boolean;
  discoveredBy: string | null;
  completedBy: string[];
  completedByCount: number;
  abandonedBy: string[];
  createdBy: string;
  createdAt: any;
  factionId: string | null;
  regionId: string;
}

/**
 * @deprecated Use getOrGenerateQuest() instead
 */
export async function getCampaignForHex(hexId: string): Promise<Campaign | null> {
  console.warn('getCampaignForHex() is deprecated. Use getOrGenerateQuest() instead.');
  return null;
}

/**
 * @deprecated Use getOrGenerateQuest() instead
 */
export async function getOrGenerateCampaign(...args: any[]): Promise<any> {
  console.warn('getOrGenerateCampaign() is deprecated. Use getOrGenerateQuest() instead.');
  throw new Error('Campaign system deprecated. Use terrain-based quest system.');
}

/**
 * @deprecated Use getQuestById() instead
 */
export async function getCampaignById(campaignId: string): Promise<any> {
  console.warn('getCampaignById() is deprecated. Use getQuestById() instead.');
  return null;
}

/**
 * @deprecated No longer needed in terrain-based system
 */
export async function getPreviousQuestNextHex(...args: any[]): Promise<any> {
  console.warn('getPreviousQuestNextHex() is deprecated.');
  return null;
}

/**
 * @deprecated No longer needed in terrain-based system
 */
export async function getNextQuestHexForPlayer(...args: any[]): Promise<any> {
  console.warn('getNextQuestHexForPlayer() is deprecated.');
  return null;
}

/**
 * @deprecated No longer needed in terrain-based system
 */
export async function shouldAutoStartQuest(...args: any[]): Promise<any> {
  console.warn('shouldAutoStartQuest() is deprecated.');
  return null;
}

/**
 * @deprecated Use getActiveQuestPlaythrough() instead
 */
export async function canPlayerStartCampaign(userId: string, hex: AxialCoord): Promise<any> {
  console.warn('canPlayerStartCampaign() is deprecated.');
  return { canStart: true };
}

/**
 * @deprecated Use getActiveQuestPlaythrough() instead
 */
export async function getQuestStatus(userId: string, hex: AxialCoord): Promise<any> {
  console.warn('getQuestStatus() is deprecated. Use getActiveQuestPlaythrough() instead.');
  const playthrough = await getActiveQuestPlaythrough(userId);
  return {
    status: playthrough ? 'in_progress' : 'not_started',
    playthrough,
    campaign: null
  };
}

/**
 * @deprecated Use abandonQuestPlaythrough() instead
 */
export async function abandonCampaign(playthroughId: string, hex: AxialCoord): Promise<{ questId: string } | null> {
  console.warn('abandonCampaign() is deprecated. Use abandonQuestPlaythrough() instead.');
  // For now, just abandon the playthrough
  const playthrough = await getQuestPlaythroughById(playthroughId);
  if (playthrough) {
    return await abandonQuestPlaythrough(playthroughId, playthrough.questId, playthrough.playerId);
  }
  return null;
}

/**
 * Get monster frequency setting from localStorage
 */
function getMonsterFrequencySetting(): number {
  const saved = localStorage.getItem('storyworld_monster_probability');
  return saved ? parseFloat(saved) : 1.0; // Default to 100% (was 1/12) - monsters appear when combat is decided
}

/**
 * Extract combat history from quest sections
 */
function extractCombatHistory(sections: QuestSection[]): Array<{ sectionNumber: number; monsterName: string }> {
  return sections
    .filter(s => s.hasCombat && s.monsterName)
    .map(s => ({
      sectionNumber: s.sectionNumber,
      monsterName: s.monsterName!
    }));
}

/**
 * @deprecated Use getOrGenerateSection() and updateQuestPlaythrough() instead
 */
export async function makeQuestChoice(
  playthroughId: string,
  choiceId: string | null,
  targetHex: AxialCoord & { terrain: string },
  adjacentHexes: Array<{ q: number; r: number; terrain: string; direction: string }>,
  visitedHexes: Array<{ q: number; r: number }>
): Promise<any> {
  console.warn('makeQuestChoice() is deprecated. Use getOrGenerateSection() instead.');
  
  // Get playthrough
  const playthrough = await getQuestPlaythroughById(playthroughId);
  if (!playthrough) {
    throw new Error('Playthrough not found');
  }
  
  // Get monster frequency setting
  const monsterFrequency = getMonsterFrequencySetting();
  console.log(`🎲 Monster frequency setting: ${(monsterFrequency * 100).toFixed(1)}%`);
  
  // Extract combat history from existing sections
  const combatHistory = extractCombatHistory(playthrough.sections || []);
  console.log(`⚔️ Combat history: ${combatHistory.length} encounters`);
  
  // Generate next section
  const nextSectionNumber = playthrough.currentSection + 1;
  
  const { section } = await getOrGenerateSection(
    playthrough.questId,
    playthrough.terrainType,
    nextSectionNumber,
    choiceId,
    targetHex,
    adjacentHexes,
    playthrough.questState,
    'brynlore',
    monsterFrequency,
    combatHistory,
    playthroughId // Pass playthrough ID for choice history tracking
  );
  
  // Update playthrough
  await updateQuestPlaythrough(playthroughId, {
    currentSection: nextSectionNumber,
    visitedHexes: [...playthrough.visitedHexes, targetHex]
  });
  
  return section;
}

/**
 * @deprecated Use completeQuestPlaythrough() instead
 */
export async function completeCampaign(campaignId: string, playthroughId: string): Promise<any> {
  console.warn('completeCampaign() is deprecated. Use completeQuestPlaythrough() instead.');
  
  const playthrough = await getQuestPlaythroughById(playthroughId);
  if (playthrough) {
    // Note: startHex parameter removed - sponsorship handled by UI
    await completeQuestPlaythrough(playthroughId, playthrough.questId, playthrough.playerId);
  }
  
  return { success: true };
}

